import { createSlice } from "@reduxjs/toolkit";

import { addWebSocketCases } from "../helpers/storeAction";
import {
  addNewRelationship,
  deleteShiftingMatrix,
  fetchShiftingMatrix,
  updateShiftingMatrix,
} from "../thunks/shiftingThunks";

import type { Relationship } from "../../utils/types/shifting";

interface ShiftingMatrixState {
  relationships: Relationship[];
  loading: boolean;
  error: string | null;
  isDataFetched?: boolean;
}

const initialState: ShiftingMatrixState = {
  relationships: [],
  loading: false,
  error: null,
  isDataFetched: false,
};

export const shiftingSlice = createSlice({
  name: "shifting",
  initialState,
  reducers: {
    resetDataFetched: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShiftingMatrix.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
      })
      .addCase(fetchShiftingMatrix.fulfilled, (state, action) => {
        state.relationships = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchShiftingMatrix.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isDataFetched = false;
        state.loading = false;
      });

    builder
      .addCase(updateShiftingMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShiftingMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.relationships = state.relationships.map((relation) => {
          if (relation.shiftingMatrixId === action.payload?.shiftingMatrixId) {
            return { ...relation, ...action.payload };
          }
          return relation;
        });
      })
      .addCase(updateShiftingMatrix.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(deleteShiftingMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteShiftingMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.relationships = state.relationships.filter(
          (relation) => relation.shiftingMatrixId !== action.payload?.id
        );
      })
      .addCase(deleteShiftingMatrix.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(addNewRelationship.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewRelationship.fulfilled, (state, action) => {
        state.loading = false;
        state.relationships = [action.payload, ...state.relationships];
      })
      .addCase(addNewRelationship.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
    addWebSocketCases(builder, "shifting", "relationships");
  },
});

export const { resetDataFetched } = shiftingSlice.actions;
export default shiftingSlice.reducer;
